import React, { useRef, useMemo } from 'react';
import { Tournament, AthleteProfile, SportType } from '../../types';
import { 
  Trophy, 
  MapPin, 
  Calendar, 
  Users, 
  ChevronLeft, 
  ChevronRight, 
  Swords, 
  Zap, 
  Sparkles,
  ArrowRight,
  Flame,
  ShieldCheck
} from 'lucide-react';

interface TrendingLocalTournamentsCarouselProps {
  user?: AthleteProfile;
  tournaments?: Tournament[];
  selectedSport?: SportType;
  registeredCity?: string;
  registeredState?: string;
  onNavigateToTab?: (tab: string) => void;
}

export const TrendingLocalTournamentsCarousel: React.FC<TrendingLocalTournamentsCarouselProps> = ({
  user,
  tournaments = [],
  selectedSport,
  registeredCity,
  registeredState,
  onNavigateToTab
}) => {
  const scrollRef = useRef<HTMLDivElement>(null);

  const city = registeredCity || user?.registeredCity || user?.location?.city || 'Venice';
  const stateCode = registeredState || user?.registeredState || user?.location?.state || 'CA';

  const allTournaments = useMemo(() => {
    return tournaments;
  }, [tournaments]);

  // Filter tournaments by selected sport and user's city or state
  const filteredTournaments = useMemo(() => {
    if (allTournaments.length === 0) return [];
    let pool = allTournaments;
    if (selectedSport) {
      const sportMatches = allTournaments.filter(t => t.sport === selectedSport);
      if (sportMatches.length > 0) {
        pool = sportMatches;
      } else {
        // Generate fallback tournaments for this sport if none exist in dataset
        const generated: Tournament[] = [
          {
            id: `tourney_gen_${selectedSport}_1`,
            title: `${city} Regional ${selectedSport.charAt(0).toUpperCase() + selectedSport.slice(1)} Open Championship`,
            sport: selectedSport,
            courtId: 'court_gen_1',
            courtName: `${city} Central Arena`,
            address: `100 Main St, ${city}, ${stateCode}`,
            description: `Official regional ${selectedSport} open championship featuring top regional seeds.`,
            city,
            state: stateCode,
            startDate: '2026-08-22',
            endDate: '2026-08-23',
            registrationDeadline: '2026-08-21T18:00:00Z',
            format: 'double_elimination',
            status: 'in_progress',
            organizerName: `${city} ${selectedSport.charAt(0).toUpperCase() + selectedSport.slice(1)} Club`,
            organizerAvatar: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?auto=format&fit=crop&q=80&w=200',
            organizerId: 'user_org_1',
            maxTeams: 8,
            prizePool: '$1,500 Cash + MVP Trophy',
            entryFee: '$30 / Team',
            registeredTeams: [
              { id: 'tm_1', name: `${city} Vanguard`, captainName: 'Alex Mercer', members: [{ id: 'u_1', name: 'Alex Mercer' }, { id: 'u_2', name: 'Jordan Hayes' }], seed: 1, wins: 2, losses: 0, pointsScored: 45, paymentStatus: 'Paid' },
              { id: 'tm_2', name: `${stateCode} Titans`, captainName: 'Marcus Vance', members: [{ id: 'u_3', name: 'Marcus Vance' }, { id: 'u_4', name: 'David Chen' }], seed: 2, wins: 1, losses: 1, pointsScored: 38, paymentStatus: 'Paid' },
              { id: 'tm_3', name: 'Coastal Elite', captainName: 'Elena Rostova', members: [{ id: 'u_5', name: 'Elena Rostova' }, { id: 'u_6', name: 'Maya Lin' }], seed: 3, wins: 1, losses: 0, pointsScored: 21, paymentStatus: 'Paid' },
              { id: 'tm_4', name: 'Sand & Turf Squad', captainName: 'Sammy Brooks', members: [{ id: 'u_7', name: 'Sammy Brooks' }, { id: 'u_8', name: 'Tyler Smith' }], seed: 4, wins: 0, losses: 1, pointsScored: 18, paymentStatus: 'Paid' },
            ],
            matches: []
          },
          {
            id: `tourney_gen_${selectedSport}_2`,
            title: `${stateCode} State ${selectedSport.charAt(0).toUpperCase() + selectedSport.slice(1)} Pro-Am Classic`,
            sport: selectedSport,
            courtId: 'court_gen_2',
            courtName: `${city} Athletic Park`,
            address: `500 Park Ave, ${city}, ${stateCode}`,
            description: `Statewide championship qualifier showcasing collegiate and elite athletic prospects.`,
            city,
            state: stateCode,
            startDate: '2026-08-29',
            endDate: '2026-08-30',
            registrationDeadline: '2026-08-28T18:00:00Z',
            format: 'single_elimination',
            status: 'upcoming',
            organizerName: `${stateCode} Athletics Association`,
            organizerAvatar: 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?auto=format&fit=crop&q=80&w=200',
            organizerId: 'user_org_2',
            maxTeams: 16,
            prizePool: '$2,500 Cash + Scout Showcase',
            entryFee: '$40 / Team',
            registeredTeams: [
              { id: 'tm_5', name: 'Apex Competitors', captainName: 'Derrick Rose', members: [{ id: 'u_9', name: 'Derrick Rose' }, { id: 'u_10', name: 'Chris Paul' }], seed: 1, wins: 0, losses: 0, pointsScored: 0, paymentStatus: 'Paid' },
              { id: 'tm_6', name: 'Metro All-Stars', captainName: 'Kevin Love', members: [{ id: 'u_11', name: 'Kevin Love' }, { id: 'u_12', name: 'Russell West' }], seed: 2, wins: 0, losses: 0, pointsScored: 0, paymentStatus: 'Paid' },
            ],
            matches: []
          }
        ];
        pool = generated;
      }
    }

    const matchedCity = pool.filter(t => 
      t.city?.toLowerCase().includes(city.toLowerCase()) || 
      city.toLowerCase().includes(t.city?.toLowerCase() || '')
    );

    if (matchedCity.length > 0) return matchedCity;

    const matchedState = pool.filter(t => 
      t.state?.toLowerCase() === stateCode.toLowerCase()
    );

    if (matchedState.length > 0) return matchedState;

    return pool;
  }, [allTournaments, selectedSport, city, stateCode]);

  // Handle Carousel Scroll
  const scroll = (direction: 'left' | 'right') => {
    if (scrollRef.current) {
      const { scrollLeft, clientWidth } = scrollRef.current;
      const scrollAmount = clientWidth * 0.75;
      scrollRef.current.scrollTo({
        left: direction === 'left' ? scrollLeft - scrollAmount : scrollLeft + scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  const getSportBadgeColor = (sport: string) => {
    switch (sport?.toLowerCase()) {
      case 'volleyball':
        return 'from-amber-400 to-amber-500 text-black';
      case 'basketball':
        return 'from-orange-500 to-amber-500 text-white';
      case 'soccer':
        return 'from-emerald-400 to-teal-500 text-black';
      case 'pickleball':
        return 'from-lime-400 to-emerald-500 text-black';
      case 'baseball':
      case 'softball':
        return 'from-rose-500 to-red-600 text-white';
      case 'football':
        return 'from-indigo-500 to-purple-600 text-white';
      case 'tennis':
        return 'from-yellow-400 to-lime-500 text-black';
      default:
        return 'from-cyan-400 to-blue-500 text-black';
    }
  };

  const getSportIcon = (sport: string) => {
    switch (sport?.toLowerCase()) {
      case 'volleyball':
        return '🏐';
      case 'basketball':
        return '🏀';
      case 'soccer':
        return '⚽';
      case 'pickleball':
        return '🏓';
      case 'baseball':
        return '⚾';
      case 'softball':
        return '🥎';
      case 'football':
        return '🏈';
      case 'tennis':
        return '🎾';
      default:
        return '🏆';
    }
  };

  const sportDisplayName = selectedSport 
    ? (selectedSport.charAt(0).toUpperCase() + selectedSport.slice(1)) 
    : '';

  return (
    <div className="bg-gradient-to-br from-indigo-950 via-slate-900 to-indigo-950 p-5 rounded-[2.5rem] border border-amber-400/30 shadow-xl space-y-4">
      {/* Header Bar */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex items-center space-x-3">
          <div className="p-2.5 bg-gradient-to-tr from-amber-400 to-orange-400 text-black rounded-2xl shadow-lg shadow-amber-400/20">
            <Swords className="w-5 h-5 stroke-[2.5]" />
          </div>
          <div>
            <div className="flex items-center space-x-2">
              <span className="text-[10px] font-mono font-black uppercase text-amber-300 bg-amber-400/20 px-2 py-0.5 rounded border border-amber-400/30">
                LOCAL ARENA BRACKETS
              </span>
              <span className="text-[10px] font-mono text-indigo-300 flex items-center gap-1">
                <MapPin className="w-3 h-3 text-lime-400" />
                {city}, {stateCode}
              </span>
            </div>
            <h3 className="text-lg font-black italic uppercase text-white mt-0.5">
              Trending Local {sportDisplayName ? `${sportDisplayName} ` : ''}Tournaments 🔥
            </h3>
          </div>
        </div>

        {/* Carousel Navigation Buttons & See All */}
        <div className="flex items-center justify-between sm:justify-end gap-2">
          {onNavigateToTab && (
            <button
              onClick={() => onNavigateToTab('tournaments')}
              className="text-xs font-mono font-black text-lime-400 hover:text-lime-300 flex items-center gap-1 transition px-3 py-1.5 bg-black/40 hover:bg-black/60 rounded-xl border border-white/10"
            >
              <span>Explore All Brackets</span>
              <ArrowRight className="w-3.5 h-3.5" />
            </button>
          )}

          <div className="flex items-center space-x-1">
            <button
              type="button"
              onClick={() => scroll('left')}
              className="p-2 bg-indigo-900/80 hover:bg-indigo-800 text-indigo-200 hover:text-white rounded-xl border border-white/10 transition"
              aria-label="Previous Tournament"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <button
              type="button"
              onClick={() => scroll('right')}
              className="p-2 bg-indigo-900/80 hover:bg-indigo-800 text-indigo-200 hover:text-white rounded-xl border border-white/10 transition"
              aria-label="Next Tournament"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Horizontal Carousel */}
      <div
        ref={scrollRef}
        className="flex items-stretch space-x-4 overflow-x-auto pb-2 pt-1 scrollbar-none snap-x snap-mandatory"
        style={{ scrollbarWidth: 'none' }}
      >
        {filteredTournaments.map((tourney) => {
          const registeredCount = tourney.registeredTeams?.length || 0;
          const maxTeams = tourney.maxTeams || 8;
          const fillPercentage = Math.min(100, Math.round((registeredCount / maxTeams) * 100));

          return (
            <div
              key={tourney.id}
              className="snap-start shrink-0 w-[300px] sm:w-[340px] bg-slate-900/90 rounded-2xl border border-white/10 hover:border-amber-400/50 p-4 transition-all duration-300 flex flex-col justify-between space-y-3 group shadow-lg"
            >
              {/* Top Row: Sport Icon & Status */}
              <div className="flex items-center justify-between">
                <span className={`px-2.5 py-1 rounded-xl text-[10px] font-black uppercase tracking-wider bg-gradient-to-r ${getSportBadgeColor(tourney.sport)} shadow`}>
                  {getSportIcon(tourney.sport)} {tourney.sport?.toUpperCase()}
                </span>

                <div className="flex items-center space-x-1">
                  {tourney.status === 'in_progress' ? (
                    <span className="px-2 py-0.5 bg-rose-500/20 text-rose-300 border border-rose-500/40 text-[9px] font-mono font-bold rounded-md flex items-center gap-1 animate-pulse">
                      <Flame className="w-3 h-3 text-rose-400" />
                      LIVE BRACKET
                    </span>
                  ) : (
                    <span className="px-2 py-0.5 bg-emerald-500/20 text-emerald-300 border border-emerald-500/40 text-[9px] font-mono font-bold rounded-md flex items-center gap-1">
                      <ShieldCheck className="w-3 h-3 text-emerald-400" />
                      OPEN ENTRY
                    </span>
                  )}
                </div>
              </div>

              {/* Tournament Title & Location */}
              <div className="space-y-1">
                <h4 className="text-base font-black italic uppercase text-white group-hover:text-amber-300 transition line-clamp-1">
                  {tourney.title}
                </h4>
                <div className="text-xs text-indigo-300/80 font-mono flex items-center gap-1">
                  <MapPin className="w-3.5 h-3.5 text-lime-400 shrink-0" />
                  <span className="truncate">{tourney.courtName || `${tourney.city} Arena`} ({tourney.city || city}, {tourney.state || stateCode})</span>
                </div>
              </div>

              {/* Prize Pool & Date Card */}
              <div className="p-2.5 bg-black/40 rounded-xl border border-white/5 grid grid-cols-2 gap-2 text-xs font-mono">
                <div>
                  <span className="text-[9px] text-indigo-300 uppercase font-black block">Prize Pool</span>
                  <span className="font-extrabold text-amber-300 text-xs truncate block">
                    {tourney.prizePool || '$500 Cash'}
                  </span>
                </div>
                <div>
                  <span className="text-[9px] text-indigo-300 uppercase font-black block">Start Date</span>
                  <span className="font-bold text-white text-xs truncate block flex items-center gap-1">
                    <Calendar className="w-3 h-3 text-cyan-400 shrink-0" />
                    {tourney.startDate}
                  </span>
                </div>
              </div>

              {/* Registered Teams Progress Bar */}
              <div className="space-y-1">
                <div className="flex justify-between text-[10px] font-mono">
                  <span className="text-indigo-300/80 font-bold flex items-center gap-1">
                    <Users className="w-3 h-3 text-lime-400" />
                    Registered Squads
                  </span>
                  <span className="text-white font-extrabold">
                    {registeredCount} / {maxTeams} Teams
                  </span>
                </div>
                <div className="w-full bg-slate-950 rounded-full h-1.5 overflow-hidden border border-white/10">
                  <div 
                    className="bg-gradient-to-r from-lime-400 to-emerald-400 h-full rounded-full transition-all duration-500"
                    style={{ width: `${fillPercentage}%` }}
                  />
                </div>
              </div>

              {/* CTA Action Button */}
              <button
                type="button"
                onClick={() => onNavigateToTab && onNavigateToTab('tournaments')}
                className="w-full py-2.5 bg-gradient-to-r from-amber-400 to-orange-400 hover:from-amber-300 hover:to-orange-300 text-black font-black italic uppercase text-xs rounded-xl transition flex items-center justify-center space-x-1.5 shadow-md shadow-amber-400/20"
              >
                <Trophy className="w-3.5 h-3.5" />
                <span>View Tournament Bracket</span>
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
};
